import { X, Minus, Plus, Trash2, Truck, Tag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { calculateCartBundleDiscount, isBundleEligible } from "@/utils/bundlePricing";
import { calculateShipping, SHIPPING_THRESHOLD } from "@/utils/shippingCalculator";

// Helper to get bundle helper text based on eligible quantity
function getBundleHelperText(eligibleQty: number): string | null {
  if (eligibleQty === 0) return null;
  if (eligibleQty === 1) return "Add 1 more to get 2 for £10";
  if (eligibleQty === 2) return "Add 1 more to get 3 for £12.95";
  return "Multi-buy discount applied ✅";
}

type CartDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();

  // Calculate bundle discount
  const bundleResult = calculateCartBundleDiscount(items);
  const subtotalAfterBundle = totalPrice - bundleResult.totalDiscount;
  
  // Calculate shipping
  const shippingResult = calculateShipping(subtotalAfterBundle);
  const orderTotal = subtotalAfterBundle + shippingResult.shippingCost;

  const handleCheckout = () => {
    if (items.length === 0) return;
    onClose(); // Close drawer first
    navigate('/checkout'); // Navigate to checkout page
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-50 h-screen w-full sm:max-w-md bg-slate-900/95 backdrop-blur-xl border-l border-slate-700/60 shadow-[0_0_80px_rgba(0,0,0,0.9)]">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-700/60 px-6 py-5 bg-slate-900/80">
            <h2 className="text-xl font-semibold text-emerald-100">
              Shopping Cart
            </h2>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-slate-300 transition-colors hover:bg-white/5 hover:text-emerald-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Cart Items - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {items.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-4 text-center px-4">
                <div className="w-16 h-16 rounded-full bg-slate-800/50 border border-slate-700/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-200 mb-1">Your cart is empty</h3>
                  <p className="text-sm text-slate-400 mb-4">Add some products to get started</p>
                </div>
                <Button
                  asChild
                  className="rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 px-8 py-5 text-slate-900 font-semibold hover:shadow-lg"
                >
                  <a href="/shop" onClick={onClose}>
                    Browse products
                  </a>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bundle Helper Text */}
                {bundleResult.eligibleItemsCount > 0 && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2">
                    <p className="text-xs sm:text-sm text-emerald-300 text-center font-medium">
                      {getBundleHelperText(bundleResult.eligibleItemsCount)}
                    </p>
                  </div>
                )}
                
                {items.map((item) => {
                  const isEligible = isBundleEligible(item.price, item.name);
                  return (
                  <div
                    key={item.id}
                    className="flex gap-4 rounded-2xl border border-slate-700/40 bg-slate-800/50 p-4 backdrop-blur-sm"
                  >
                    <div className="flex-1">
                      <h3 className="font-medium text-emerald-100">
                        {item.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-slate-300">
                          £{item.price.toFixed(2)}
                        </p>
                        {isEligible && (
                          <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                            Multi-buy
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 rounded-full border border-slate-700/50 bg-slate-900/50 px-3 py-1">
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity - 1)
                          }
                          className="text-slate-300 transition-colors hover:text-emerald-300"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="min-w-[1.5rem] text-center text-sm text-slate-200">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.quantity + 1)
                          }
                          className="text-slate-300 transition-colors hover:text-emerald-300"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.id)}
                        className="rounded-full p-2 text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - Fixed at bottom */}
          {items.length > 0 && (
            <div className="border-t border-slate-700/60 bg-slate-900/90 px-6 py-5 backdrop-blur-sm">
              {/* Subtotal */}
              <div className="flex items-center justify-between mb-2 text-sm">
                <span className="text-slate-300">Subtotal</span>
                <span className="text-slate-100">£{totalPrice.toFixed(2)}</span>
              </div>
              
              {/* Bundle Discount */}
              {bundleResult.totalDiscount > 0 && (
                <div className="flex items-center justify-between mb-2 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5 text-emerald-400" />
                    <span className="text-emerald-400">Multi-buy discount</span>
                  </div>
                  <span className="text-emerald-400">-£{bundleResult.totalDiscount.toFixed(2)}</span>
                </div>
              )}
              
              {/* Shipping */}
              <div className="flex items-center justify-between mb-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="text-slate-300">Shipping</span>
                </div>
                {shippingResult.isFreeShipping ? (
                  <span className="text-emerald-400 font-medium">FREE</span>
                ) : (
                  <span className="text-slate-100">£{shippingResult.shippingCost.toFixed(2)}</span>
                )}
              </div>
              
              {/* Progress to free shipping */}
              {!shippingResult.isFreeShipping && (
                <p className="text-xs text-slate-400 mb-3">
                  {shippingResult.message}
                </p>
              )}
              
              {/* Order Total */}
              <div className="flex items-center justify-between mb-4 pt-2 border-t border-slate-700/50">
                <span className="text-lg font-medium text-slate-200">Total</span>
                <span className="text-2xl font-semibold text-emerald-300">
                  £{orderTotal.toFixed(2)}
                </span>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={handleCheckout}
                  className="w-full rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 py-6 text-base font-medium text-slate-900 shadow-[0_0_40px_rgba(45,255,196,0.35)] transition-all hover:shadow-[0_0_55px_rgba(45,255,196,0.55)]"
                >
                  Proceed to checkout
                </Button>
                <button
                  onClick={clearCart}
                  className="w-full text-sm text-slate-400 transition-colors hover:text-red-400"
                >
                  Clear cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
